import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  ComplaintCreditNoteRef,
  ComplaintFilters,
  ComplaintReportSummary,
  ComplaintStage,
  ContractTier,
  CreateComplaintPayload,
  CreateTicketPayload,
  ITAsset,
  KnowledgeArticle,
  MonitoringIncident,
  SalesInvoiceRef,
  ServiceComplaint,
  ServiceDeskSummary,
  ServiceTicket,
  SourceChannel,
  TicketCategory,
  TicketFilters,
  TicketListResponse,
} from './types';

// Statuses AccountsSalesInvoiceModel uses once an invoice is finalized (a
// draft has nothing posted to a ledger yet, so it cannot be credited).
const CREDITABLE_INVOICE_STATUSES = new Set(['issued', 'unpaid', 'partially_paid', 'paid', 'overdue']);

const BASE = '/api/service-desk';

export const serviceDeskKeys = {
  root: ['service-desk'] as const,
  summary: ['service-desk', 'summary'] as const,
  tickets: (filters: TicketFilters) => ['service-desk', 'tickets', filters] as const,
  ticket: (id: string | null) => ['service-desk', 'ticket', id] as const,
  complaints: (filters: ComplaintFilters) => ['service-desk', 'complaints', filters] as const,
  complaint: (id: string | null) => ['service-desk', 'complaint', id] as const,
  complaintSummary: ['service-desk', 'complaint-summary'] as const,
  knowledge: (category: string) => ['service-desk', 'knowledge', category] as const,
  assets: ['service-desk', 'assets'] as const,
  incidents: ['service-desk', 'incidents'] as const,
};

export function useServiceDeskSummary() {
  return useQuery({
    queryKey: serviceDeskKeys.summary,
    queryFn: async () => (await api.get<ServiceDeskSummary>(`${BASE}/summary?workflowType=standard`)).data,
    staleTime: 30_000,
  });
}

export function useServiceTickets(filters: TicketFilters) {
  return useQuery({
    queryKey: serviceDeskKeys.tickets(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.kind) params.set('kind', filters.kind);
      if (filters.status) params.set('status', filters.status);
      if (filters.priority) params.set('priority', filters.priority);
      params.set('limit', '100');
      return (await api.get<TicketListResponse>(`${BASE}/tickets?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useServiceTicket(id: string | null) {
  return useQuery({
    queryKey: serviceDeskKeys.ticket(id),
    queryFn: async () => (await api.get<ServiceTicket>(`${BASE}/tickets/${id}`)).data,
    enabled: !!id,
  });
}

export function useComplaintReportSummary() {
  return useQuery({
    queryKey: serviceDeskKeys.complaintSummary,
    queryFn: async () => (await api.get<ComplaintReportSummary>(`${BASE}/complaints/reports/summary`)).data,
    staleTime: 30_000,
  });
}

export function useServiceComplaints(filters: ComplaintFilters) {
  return useQuery({
    queryKey: serviceDeskKeys.complaints(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.cmsStatus) params.set('cmsStatus', filters.cmsStatus);
      if (filters.cmsStage) params.set('cmsStage', filters.cmsStage);
      if (filters.cmsCategory) params.set('cmsCategory', filters.cmsCategory);
      if (filters.complaintTypeId) params.set('complaintTypeId', filters.complaintTypeId);
      if (filters.batchNumber) params.set('batchNumber', filters.batchNumber);
      params.set('limit', '100');
      return (await api.get<{ data: ServiceComplaint[]; total: number; limit: number; offset: number }>(`${BASE}/complaints?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useServiceComplaint(id: string | null) {
  return useQuery({
    queryKey: serviceDeskKeys.complaint(id),
    queryFn: async () => (await api.get<ServiceComplaint>(`${BASE}/complaints/${id}`)).data,
    enabled: !!id,
  });
}

/**
 * Recent posted (or partially-paid/paid) SalesInvoiceV2 invoices — the only
 * ones assertComplaintCanMoveToCreditNote / return-service.js will accept as
 * a credit note's originalInvoiceId. Powers the "Link Invoice" picker on a
 * complaint; without a linked invoiceId the credit-note route refuses
 * (src/shared/complaints/credit-note.js). Not filtered by the complaint's
 * dealer server-side — see the customerId caveat on SalesInvoiceRef in
 * types.ts — so the picker shows customerName and leans on search.
 */
export function useComplaintEligibleInvoices(enabled = true) {
  return useQuery({
    queryKey: ['service-desk', 'complaint-eligible-invoices'],
    queryFn: async () => {
      const { data } = await api.get<{ data: { items: SalesInvoiceRef[] } }>(`/api/accounting/sales/invoices?limit=100`);
      return (data.data?.items ?? []).filter((invoice) => CREDITABLE_INVOICE_STATUSES.has(invoice.status));
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useKnowledgeArticles(category = '', enabled = true) {
  return useQuery({
    queryKey: serviceDeskKeys.knowledge(category),
    queryFn: async () => {
      const suffix = category ? `?category=${encodeURIComponent(category)}` : '';
      return (await api.get<KnowledgeArticle[]>(`${BASE}/knowledge${suffix}`)).data;
    },
    enabled,
  });
}

export function useITAssets(enabled = true) {
  return useQuery({
    queryKey: serviceDeskKeys.assets,
    queryFn: async () => (await api.get<ITAsset[]>(`${BASE}/assets`)).data,
    enabled,
  });
}

export function useMonitoringIncidents(enabled = true) {
  return useQuery({
    queryKey: serviceDeskKeys.incidents,
    queryFn: async () => (await api.get<MonitoringIncident[]>(`${BASE}/incidents`)).data,
    enabled,
  });
}

export function useServiceDeskMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: serviceDeskKeys.root });

  const createTicket = useMutation({
    mutationFn: async (payload: CreateTicketPayload) => (await api.post<ServiceTicket>(`${BASE}/tickets`, payload)).data,
    onSuccess: refresh,
  });
  const createComplaint = useMutation({
    mutationFn: async (payload: CreateComplaintPayload) => (await api.post<ServiceComplaint>(`${BASE}/complaints`, payload)).data,
    onSuccess: refresh,
  });
  const updateComplaint = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      (await api.patch<ServiceComplaint>(`${BASE}/complaints/${id}`, payload)).data,
    onSuccess: refresh,
  });
  const transitionComplaint = useMutation({
    mutationFn: async ({ id, toStatus, note }: { id: string; toStatus: ServiceComplaint['cmsStatus']; note?: string }) =>
      (await api.post<ServiceComplaint>(`${BASE}/complaints/${id}/transition`, { toStatus, note })).data,
    onSuccess: refresh,
  });
  // Additive sibling of transitionComplaint above, which is left as it is: that
  // one drives the frozen 5-state `cmsStatus`, this one drives the 8-step
  // `cmsStage`. Both return the same complaint payload.
  const transitionComplaintStage = useMutation({
    mutationFn: async ({ id, toStage, note }: { id: string; toStage: ComplaintStage; note?: string }) =>
      (await api.post<ServiceComplaint>(`${BASE}/complaints/${id}/stage`, { toStage, note })).data,
    onSuccess: refresh,
  });
  // Multipart upload, declared the same way every other upload in this app
  // declares it (see ItemMasterListPage's image upload). The shared axios client
  // sets a JSON Content-Type by default, so it must be overridden here or multer
  // never sees a multipart body; axios replaces this value with one carrying the
  // browser's own boundary.
  const uploadComplaintAttachments = useMutation({
    mutationFn: async ({ id, files, captions }: { id: string; files: File[]; captions?: string[] }) => {
      const body = new FormData();
      files.forEach((file) => body.append('files', file));
      (captions ?? []).forEach((caption) => body.append('captions', caption));
      return (await api.post<ServiceComplaint>(`${BASE}/complaints/${id}/attachments`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data;
    },
    onSuccess: refresh,
  });
  const deleteComplaintAttachment = useMutation({
    mutationFn: async ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      (await api.delete<ServiceComplaint>(`${BASE}/complaints/${id}/attachments/${attachmentId}`)).data,
    onSuccess: refresh,
  });
  const raiseComplaintCreditNote = useMutation({
    mutationFn: async ({ id, quantity, unitPriceMinor, settlementType, note }: {
      id: string;
      quantity?: number;
      unitPriceMinor?: number;
      settlementType?: ComplaintCreditNoteRef['settlementType'];
      note?: string;
    }) => (await api.post<{ creditNote: ComplaintCreditNoteRef; complaint: ServiceComplaint }>(
      `${BASE}/complaints/${id}/credit-note`,
      { quantity, unitPriceMinor, settlementType, note },
    )).data,
    onSuccess: refresh,
  });

  const assignTicket = useMutation({
    mutationFn: async ({ id, assignedTo, assignedTeam, note }: { id: string; assignedTo: string; assignedTeam: string; note?: string }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/assign`, { assignedTo, assignedTeam, note })).data,
    onSuccess: refresh,
  });
  const respondTicket = useMutation({
    mutationFn: async ({ id, note, channel }: { id: string; note: string; channel: SourceChannel }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/respond`, { note, channel })).data,
    onSuccess: refresh,
  });
  const updateTicketStatus = useMutation({
    mutationFn: async ({ id, status, note }: { id: string; status: 'in_progress' | 'waiting_requester'; note?: string }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/status`, { status, note })).data,
    onSuccess: refresh,
  });
  const escalateTicket = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/escalate`, { note })).data,
    onSuccess: refresh,
  });
  const resolveTicket = useMutation({
    mutationFn: async ({ id, resolutionSummary, createKnowledgeDraft }: { id: string; resolutionSummary: string; createKnowledgeDraft: boolean }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/resolve`, { resolutionSummary, createKnowledgeDraft })).data,
    onSuccess: refresh,
  });
  const confirmTicket = useMutation({
    mutationFn: async ({ id, csat, note }: { id: string; csat?: number; note?: string }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/confirm`, { csat, note })).data,
    onSuccess: refresh,
  });

  const createKnowledge = useMutation({
    mutationFn: async (payload: { title: string; body: string; category: TicketCategory; visibility: 'internal' | 'client' }) =>
      (await api.post<KnowledgeArticle>(`${BASE}/knowledge`, payload)).data,
    onSuccess: refresh,
  });
  const publishKnowledge = useMutation({
    mutationFn: async (id: string) => (await api.post<KnowledgeArticle>(`${BASE}/knowledge/${id}/publish`)).data,
    onSuccess: refresh,
  });

  const createAsset = useMutation({
    mutationFn: async (payload: {
      assetTag: string;
      name: string;
      assetType: ITAsset['assetType'];
      serialNumber?: string;
      warrantyExpiresAt?: string;
      licenseExpiresAt?: string;
      maintenanceDueAt?: string;
      purchaseCostMinor?: number;
      currency?: string;
      notes?: string;
    }) => (await api.post<ITAsset>(`${BASE}/assets`, payload)).data,
    onSuccess: refresh,
  });
  const assignAsset = useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) =>
      (await api.post<ITAsset>(`${BASE}/assets/${id}/assign`, { userId })).data,
    onSuccess: refresh,
  });
  const returnAsset = useMutation({
    mutationFn: async (id: string) => (await api.post<ITAsset>(`${BASE}/assets/${id}/return`)).data,
    onSuccess: refresh,
  });
  const retireAsset = useMutation({
    mutationFn: async (id: string) => (await api.post<ITAsset>(`${BASE}/assets/${id}/retire`)).data,
    onSuccess: refresh,
  });

  const createIncident = useMutation({
    mutationFn: async (payload: {
      serviceName: string;
      source: MonitoringIncident['source'];
      alertType: MonitoringIncident['alertType'];
      severity: MonitoringIncident['severity'];
      summary: string;
    }) => (await api.post<MonitoringIncident & { ticket: ServiceTicket }>(`${BASE}/incidents`, payload)).data,
    onSuccess: refresh,
  });
  const resolveIncident = useMutation({
    mutationFn: async ({ id, rootCause }: { id: string; rootCause: string }) =>
      (await api.post<MonitoringIncident>(`${BASE}/incidents/${id}/resolve`, { rootCause })).data,
    onSuccess: refresh,
  });

  return {
    createTicket,
    createComplaint,
    updateComplaint,
    transitionComplaint,
    transitionComplaintStage,
    uploadComplaintAttachments,
    deleteComplaintAttachment,
    raiseComplaintCreditNote,
    assignTicket,
    respondTicket,
    updateTicketStatus,
    escalateTicket,
    resolveTicket,
    confirmTicket,
    createKnowledge,
    publishKnowledge,
    createAsset,
    assignAsset,
    returnAsset,
    retireAsset,
    createIncident,
    resolveIncident,
  };
}

/**
 * Same-origin URL that streams one complaint attachment.
 *
 * Usable directly as an <img>/<video>/<iframe> source: the shared axios client
 * documents that this app uses cookie auth (crm_token) over same-origin
 * requests, so the browser sends the session cookie on a plain media request and
 * the auth-gated route accepts it. Nothing about the bytes is public — the route
 * still resolves the complaint through the same tenant/company read scope as
 * every other complaint read.
 */
export function complaintAttachmentUrl(complaintId: string, attachmentId: string) {
  return `${BASE}/complaints/${complaintId}/attachments/${attachmentId}`;
}

export type { ContractTier };
